import React, { useState, useEffect, useCallback } from "react";
import {
  PageContainer,
  PageHeader,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Button,
  Input,
  Label,
  Badge,
  Alert,
  AlertTitle,
  AlertDescription,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@homy/ui";
import {
  ShieldCheck,
  ShieldAlert,
  KeyRound,
  Lock,
  QrCode,
  Copy,
  Check,
  CheckCircle2,
  AlertCircle,
  Eye,
  EyeOff,
  Download,
  RefreshCw,
  Smartphone,
} from "lucide-react";
import {
  changePassword,
  getMfaStatus,
  setupTotp,
  confirmTotp,
  disableMfa,
} from "../../../lib/api/client";

// --- Pure JavaScript QR Code SVG Generator (ISO/IEC 18004 Level M) ---
const EXP_TABLE = new Uint8Array(512);
const LOG_TABLE = new Uint8Array(256);

(function initGaloisField() {
  let val = 1;
  for (let i = 0; i < 255; i++) {
    EXP_TABLE[i] = val;
    EXP_TABLE[i + 255] = val;
    LOG_TABLE[val] = i;
    val <<= 1;
    if (val & 0x100) {
      val ^= 0x11d;
    }
  }
})();

function gfMul(x, y) {
  if (x === 0 || y === 0) return 0;
  return EXP_TABLE[LOG_TABLE[x] + LOG_TABLE[y]];
}

function rsGeneratorPoly(numEcCodewords) {
  let poly = new Uint8Array([1]);
  for (let i = 0; i < numEcCodewords; i++) {
    const next = new Uint8Array(poly.length + 1);
    const factor = EXP_TABLE[i];
    for (let j = 0; j < poly.length; j++) {
      next[j] ^= gfMul(poly[j], factor);
      next[j + 1] ^= poly[j];
    }
    poly = next;
  }
  return poly;
}

function rsCalculate(data, numEcCodewords) {
  const gen = rsGeneratorPoly(numEcCodewords);
  const remainder = new Uint8Array(numEcCodewords);
  for (let i = 0; i < data.length; i++) {
    const factor = data[i] ^ remainder[0];
    for (let j = 0; j < numEcCodewords - 1; j++) {
      remainder[j] = remainder[j + 1] ^ gfMul(gen[j], factor);
    }
    remainder[numEcCodewords - 1] = gfMul(gen[numEcCodewords - 1], factor);
  }
  return remainder;
}

const QR_TABLE_M = [
  null,
  [26, 10, 1, 16, 0, 0],
  [44, 16, 1, 28, 0, 0],
  [70, 26, 1, 44, 0, 0],
  [100, 18, 2, 32, 0, 0],
  [134, 24, 2, 43, 0, 0],
  [172, 16, 4, 27, 0, 0],
  [196, 18, 2, 31, 4, 32],
  [242, 22, 2, 38, 4, 39],
  [292, 22, 3, 36, 5, 37],
  [346, 26, 4, 43, 5, 44],
];

const ALIGNMENT_COORDS = [
  [],
  [],
  [6, 18],
  [6, 22],
  [6, 26],
  [6, 30],
  [6, 34],
  [6, 22, 38],
  [6, 24, 42],
  [6, 26, 46],
  [6, 28, 50],
];

function getFormatBits(mask) {
  const data = (0 << 3) | mask;
  let rem = data << 10;
  for (let i = 4; i >= 0; i--) {
    if ((rem >> (i + 10)) & 1) {
      rem ^= 0x537 << i;
    }
  }
  return ((data << 10) | rem) ^ 0x5412;
}

function encodeQrData(text, version) {
  const encoder = new TextEncoder();
  const utf8Bytes = encoder.encode(text);
  const dataLength = utf8Bytes.length;
  const table = QR_TABLE_M[version];
  const totalDataCodewords = table[2] * table[3] + table[4] * table[5];

  const bitBuffer = [];
  function putBits(val, len) {
    for (let i = len - 1; i >= 0; i--) {
      bitBuffer.push((val >> i) & 1);
    }
  }

  putBits(0b0100, 4);
  const countBits = version < 10 ? 8 : 16;
  putBits(dataLength, countBits);

  for (let i = 0; i < dataLength; i++) {
    putBits(utf8Bytes[i], 8);
  }

  const capacityBits = totalDataCodewords * 8;
  const termLength = Math.min(4, capacityBits - bitBuffer.length);
  putBits(0, termLength);

  while (bitBuffer.length % 8 !== 0) {
    bitBuffer.push(0);
  }

  const bytes = new Uint8Array(totalDataCodewords);
  const fullBytes = bitBuffer.length / 8;
  for (let i = 0; i < fullBytes; i++) {
    let b = 0;
    for (let j = 0; j < 8; j++) {
      b = (b << 1) | bitBuffer[i * 8 + j];
    }
    bytes[i] = b;
  }

  const padBytes = [0xec, 0x11];
  let padIdx = 0;
  for (let i = fullBytes; i < totalDataCodewords; i++) {
    bytes[i] = padBytes[padIdx % 2];
    padIdx++;
  }

  return bytes;
}

function generateQrMatrix(text) {
  const encoder = new TextEncoder();
  const byteCount = encoder.encode(text).length;

  let version = 1;
  while (version <= 10) {
    const table = QR_TABLE_M[version];
    const maxData = table[2] * table[3] + table[4] * table[5];
    const headerBits = 4 + (version < 10 ? 8 : 16);
    const maxBytes = Math.floor((maxData * 8 - headerBits) / 8);
    if (byteCount <= maxBytes) break;
    version++;
  }
  if (version > 10) version = 10;

  const dataCodewords = encodeQrData(text, version);
  const table = QR_TABLE_M[version];
  const numBlocksG1 = table[2];
  const dataLenG1 = table[3];
  const numBlocksG2 = table[4];
  const dataLenG2 = table[5];
  const ecPerBlock = table[1];

  const blocks = [];
  let offset = 0;
  for (let b = 0; b < numBlocksG1; b++) {
    const data = dataCodewords.slice(offset, offset + dataLenG1);
    const ec = rsCalculate(data, ecPerBlock);
    blocks.push({ data, ec });
    offset += dataLenG1;
  }
  for (let b = 0; b < numBlocksG2; b++) {
    const data = dataCodewords.slice(offset, offset + dataLenG2);
    const ec = rsCalculate(data, ecPerBlock);
    blocks.push({ data, ec });
    offset += dataLenG2;
  }

  const interleaved = [];
  const maxDataLen = Math.max(dataLenG1, dataLenG2);
  for (let i = 0; i < maxDataLen; i++) {
    for (const blk of blocks) {
      if (i < blk.data.length) interleaved.push(blk.data[i]);
    }
  }
  for (let i = 0; i < ecPerBlock; i++) {
    for (const blk of blocks) {
      interleaved.push(blk.ec[i]);
    }
  }

  const size = 17 + 4 * version;
  const matrix = Array.from({ length: size }, () => Array(size).fill(false));
  const isFunction = Array.from({ length: size }, () => Array(size).fill(false));

  function setFinder(row, col) {
    for (let r = -1; r <= 7; r++) {
      for (let c = -1; c <= 7; c++) {
        const nr = row + r;
        const nc = col + c;
        if (nr >= 0 && nr < size && nc >= 0 && nc < size) {
          isFunction[nr][nc] = true;
          if (r >= 0 && r <= 6 && c >= 0 && c <= 6) {
            matrix[nr][nc] = r === 0 || r === 6 || c === 0 || c === 6 || (r >= 2 && r <= 4 && c >= 2 && c <= 4);
          } else {
            matrix[nr][nc] = false;
          }
        }
      }
    }
  }

  setFinder(0, 0);
  setFinder(0, size - 7);
  setFinder(size - 7, 0);

  for (let i = 8; i < size - 8; i++) {
    isFunction[6][i] = true;
    matrix[6][i] = i % 2 === 0;
    isFunction[i][6] = true;
    matrix[i][6] = i % 2 === 0;
  }

  const align = ALIGNMENT_COORDS[version];
  for (let i = 0; i < align.length; i++) {
    for (let j = 0; j < align.length; j++) {
      const ar = align[i];
      const ac = align[j];
      if (isFunction[ar][ac]) continue;
      for (let r = -2; r <= 2; r++) {
        for (let c = -2; c <= 2; c++) {
          isFunction[ar + r][ac + c] = true;
          matrix[ar + r][ac + c] = Math.abs(r) === 2 || Math.abs(c) === 2 || (r === 0 && c === 0);
        }
      }
    }
  }

  isFunction[4 * version + 9][8] = true;
  matrix[4 * version + 9][8] = true;

  for (let i = 0; i < 9; i++) {
    isFunction[8][i] = true;
    isFunction[i][8] = true;
    isFunction[8][size - 1 - i] = true;
    isFunction[size - 1 - i][8] = true;
  }

  let bitIdx = 0;
  const totalBits = interleaved.length * 8;
  let dir = -1;
  let row = size - 1;
  let col = size - 1;

  while (col > 0) {
    if (col === 6) col--;
    for (let r = 0; r < size; r++) {
      const curRow = dir === -1 ? size - 1 - r : r;
      for (let cOffset = 0; cOffset < 2; cOffset++) {
        const curCol = col - cOffset;
        if (!isFunction[curRow][curCol]) {
          let bit = false;
          if (bitIdx < totalBits) {
            const bytePos = Math.floor(bitIdx / 8);
            const bitPos = 7 - (bitIdx % 8);
            bit = ((interleaved[bytePos] >> bitPos) & 1) === 1;
            bitIdx++;
          }
          matrix[curRow][curCol] = bit;
        }
      }
    }
    dir = -dir;
    col -= 2;
  }

  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (!isFunction[r][c] && (r + c) % 2 === 0) {
        matrix[r][c] = !matrix[r][c];
      }
    }
  }

  const formatBits = getFormatBits(0);
  const formatCoordsTL = [
    [8, 0], [8, 1], [8, 2], [8, 3], [8, 4], [8, 5],
    [8, 7], [8, 8], [7, 8], [5, 8], [4, 8], [3, 8],
    [2, 8], [1, 8], [0, 8]
  ];
  for (let i = 0; i < 15; i++) {
    matrix[formatCoordsTL[i][0]][formatCoordsTL[i][1]] = ((formatBits >> (14 - i)) & 1) === 1;
  }
  for (let i = 0; i < 7; i++) {
    matrix[size - 1 - i][8] = ((formatBits >> i) & 1) === 1;
  }
  for (let i = 7; i < 15; i++) {
    matrix[8][size - 15 + i] = ((formatBits >> i) & 1) === 1;
  }

  return matrix;
}

function QrCodeSvg({ value, size = 180 }) {
  if (!value) return null;
  try {
    const matrix = generateQrMatrix(value);
    const matrixSize = matrix.length;
    let path = "";
    for (let r = 0; r < matrixSize; r++) {
      for (let c = 0; c < matrixSize; c++) {
        if (matrix[r][c]) {
          path += `M${c},${r}h1v1h-1z `;
        }
      }
    }

    return (
      <div className="bg-white p-3 rounded-xl border border-border inline-block shadow-sm">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox={`-2 -2 ${matrixSize + 4} ${matrixSize + 4}`}
          width={size}
          height={size}
          className="block"
          shapeRendering="crispEdges"
        >
          <path d={path} fill="#000000" />
        </svg>
      </div>
    );
  } catch (err) {
    return (
      <div className="p-4 text-center text-xs text-muted-foreground border border-dashed rounded-xl">
        Failed to render QR Code: {err.message}
      </div>
    );
  }
}

export function SecuritySettingsPage() {
  // Password State
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(null);
  const [passwordError, setPasswordError] = useState(null);

  // MFA State
  const [mfaStatus, setMfaStatus] = useState(null);
  const [mfaLoading, setMfaLoading] = useState(true);
  const [setupModalOpen, setSetupModalOpen] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyError, setVerifyError] = useState(null);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [backupCodesModalOpen, setBackupCodesModalOpen] = useState(false);
  const [backupCodesList, setBackupCodesList] = useState([]);
  const [copiedBackupCodes, setCopiedBackupCodes] = useState(false);

  // Disable Modal State
  const [disableModalOpen, setDisableModalOpen] = useState(false);
  const [disablePassword, setDisablePassword] = useState("");
  const [disableLoading, setDisableLoading] = useState(false);
  const [disableError, setDisableError] = useState(null);

  // Fetch MFA Status
  const fetchMfaStatus = useCallback(async () => {
    try {
      setMfaLoading(true);
      const data = await getMfaStatus();
      setMfaStatus(data);
    } catch (err) {
      console.error("Failed to load MFA status:", err);
    } finally {
      setMfaLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMfaStatus();
  }, [fetchMfaStatus]);

  // Real-time Password Rules
  const hasMinLength = passwordForm.newPassword.length >= 8;
  const hasUppercase = /[A-ZÁÉÍÓÚÑ]/.test(passwordForm.newPassword);
  const hasNumber = /\d/.test(passwordForm.newPassword);
  const passwordsMatch =
    passwordForm.newPassword &&
    passwordForm.confirmPassword &&
    passwordForm.newPassword === passwordForm.confirmPassword;
  const isPasswordValid = hasMinLength && hasUppercase && hasNumber && passwordsMatch;

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(null);

    if (!passwordsMatch) {
      setPasswordError("New passwords do not match.");
      return;
    }
    if (!hasMinLength || !hasUppercase || !hasNumber) {
      setPasswordError("Password must be at least 8 characters, include an uppercase letter and a number.");
      return;
    }

    try {
      setPasswordLoading(true);
      const res = await changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
        confirmPassword: passwordForm.confirmPassword,
      });
      setPasswordSuccess(res.message || "Password changed successfully.");
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.message || err.message || "Failed to update password.";
      setPasswordError(msg);
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleStartSetup = async () => {
    setVerifyError(null);
    setVerifyCode("");
    try {
      setMfaLoading(true);
      const data = await setupTotp();
      setSetupData(data);
      setBackupCodesList(data.backupCodes || []);
      setSetupModalOpen(true);
    } catch (err) {
      console.error("Failed to start TOTP setup:", err);
    } finally {
      setMfaLoading(false);
    }
  };

  const handleConfirmTotp = async (e) => {
    e.preventDefault();
    setVerifyError(null);
    if (!verifyCode.trim() || verifyCode.trim().length !== 6) {
      setVerifyError("Please enter a valid 6-digit code.");
      return;
    }

    try {
      setVerifyLoading(true);
      await confirmTotp(verifyCode.trim());
      setSetupModalOpen(false);
      setBackupCodesModalOpen(true);
      await fetchMfaStatus();
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.message || "Invalid verification code.";
      setVerifyError(msg);
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleDisableMfa = async (e) => {
    e.preventDefault();
    setDisableError(null);
    if (!disablePassword) {
      setDisableError("Please enter your current administrator password.");
      return;
    }

    try {
      setDisableLoading(true);
      await disableMfa(disablePassword);
      setDisableModalOpen(false);
      setDisablePassword("");
      await fetchMfaStatus();
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.message || "Invalid administrator password.";
      setDisableError(msg);
    } finally {
      setDisableLoading(false);
    }
  };

  const copyToClipboard = (text, setCopiedFn) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      setCopiedFn(true);
      setTimeout(() => setCopiedFn(false), 2000);
    }
  };

  const downloadBackupCodes = () => {
    const content = `HomyOS Platform Admin Backup Recovery Codes\nGenerated: ${new Date().toISOString()}\n\n` +
      backupCodesList.map((c, i) => `${i + 1}. ${c}`).join("\n") +
      "\n\nEach code can be used once. Store these safely in your password manager.";
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `homyos-admin-backup-codes-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const isMfaActive = mfaStatus?.isMfaEnabled || mfaStatus?.is_mfa_enabled;

  return (
    <PageContainer>
      <PageHeader
        title="Admin Security Settings"
        description="Manage your platform administrator credentials, step-up verification, and multi-factor authentication (MFA)."
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-6">
        {/* Card 1: Password Management */}
        <Card className="border-border/80 shadow-sm flex flex-col justify-between">
          <div>
            <CardHeader className="pb-4">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-primary/10 text-primary">
                  <KeyRound className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-lg">Change Admin Password</CardTitle>
                  <CardDescription>
                    Update your account password with strong complexity rules.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-4 pt-2">
              {passwordSuccess && (
                <Alert variant="success" className="mb-2">
                  <CheckCircle2 className="h-4 w-4" />
                  <AlertTitle>Success</AlertTitle>
                  <AlertDescription>{passwordSuccess}</AlertDescription>
                </Alert>
              )}

              {passwordError && (
                <Alert variant="destructive" className="mb-2">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Password Error</AlertTitle>
                  <AlertDescription>{passwordError}</AlertDescription>
                </Alert>
              )}

              <form id="password-change-form" onSubmit={handlePasswordSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="current-password">Current Password</Label>
                  <div className="relative">
                    <Input
                      id="current-password"
                      type={showCurrentPassword ? "text" : "password"}
                      value={passwordForm.currentPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                      placeholder="••••••••••••"
                      required
                      className="pr-10 font-mono text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="new-password">New Password</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showNewPassword ? "text" : "password"}
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                      placeholder="••••••••••••"
                      required
                      className="pr-10 font-mono text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="confirm-password">Confirm New Password</Label>
                  <Input
                    id="confirm-password"
                    type={showNewPassword ? "text" : "password"}
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                    placeholder="••••••••••••"
                    required
                    className="font-mono text-sm"
                  />
                </div>

                {/* Password Strength Checklist */}
                <div className="rounded-lg border border-border/60 bg-muted/20 p-3 space-y-1.5 text-xs">
                  <p className="font-medium text-foreground mb-1">Password Requirements:</p>
                  <div className="flex items-center gap-2">
                    <span className={hasMinLength ? "text-emerald-500" : "text-muted-foreground"}>
                      {hasMinLength ? <Check className="h-3.5 w-3.5" /> : "•"}
                    </span>
                    <span className={hasMinLength ? "text-foreground font-medium" : "text-muted-foreground"}>
                      Minimum 8 characters
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={hasUppercase ? "text-emerald-500" : "text-muted-foreground"}>
                      {hasUppercase ? <Check className="h-3.5 w-3.5" /> : "•"}
                    </span>
                    <span className={hasUppercase ? "text-foreground font-medium" : "text-muted-foreground"}>
                      At least one uppercase letter (A-Z)
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={hasNumber ? "text-emerald-500" : "text-muted-foreground"}>
                      {hasNumber ? <Check className="h-3.5 w-3.5" /> : "•"}
                    </span>
                    <span className={hasNumber ? "text-foreground font-medium" : "text-muted-foreground"}>
                      At least one number (0-9)
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={passwordsMatch ? "text-emerald-500" : "text-muted-foreground"}>
                      {passwordsMatch ? <Check className="h-3.5 w-3.5" /> : "•"}
                    </span>
                    <span className={passwordsMatch ? "text-foreground font-medium" : "text-muted-foreground"}>
                      Passwords match
                    </span>
                  </div>
                </div>
              </form>
            </CardContent>
          </div>

          <CardFooter className="pt-4 border-t border-border/40">
            <Button
              type="submit"
              form="password-change-form"
              disabled={passwordLoading || !passwordForm.currentPassword || !isPasswordValid}
              className="w-full gap-2"
            >
              {passwordLoading ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Updating Password...
                </>
              ) : (
                <>
                  <Lock className="h-4 w-4" />
                  Update Password
                </>
              )}
            </Button>
          </CardFooter>
        </Card>

        {/* Card 2: Two-Factor Authentication (MFA) */}
        <Card className="border-border/80 shadow-sm flex flex-col justify-between">
          <div>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-amber-500/10 text-amber-500">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Two-Factor Authentication (2FA)</CardTitle>
                    <CardDescription>
                      Secure your platform admin access using an authenticator app.
                    </CardDescription>
                  </div>
                </div>

                {mfaLoading ? (
                  <Badge variant="outline" className="animate-pulse">Checking...</Badge>
                ) : isMfaActive ? (
                  <Badge variant="success" className="gap-1 bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30">
                    <CheckCircle2 className="h-3 w-3" />
                    Enforced & Active
                  </Badge>
                ) : (
                  <Badge variant="outline" className="gap-1 bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30">
                    <ShieldAlert className="h-3 w-3" />
                    Disabled / Optional
                  </Badge>
                )}
              </div>
            </CardHeader>

            <CardContent className="space-y-4 pt-2">
              <div className="rounded-lg border border-border/80 bg-muted/20 p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <Smartphone className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-foreground">TOTP Authenticator Apps</h4>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                      Use applications like 1Password, Google Authenticator, Microsoft Authenticator, or Apple Keychain to generate time-based one-time passwords (RFC 6238).
                    </p>
                  </div>
                </div>

                <div className="pt-3 border-t border-border/40 grid grid-cols-2 gap-4 text-xs">
                  <div>
                    <span className="text-muted-foreground block text-[11px]">Enforcement Method:</span>
                    <span className="font-medium text-foreground">
                      {isMfaActive ? mfaStatus?.mfaMethod || "TOTP" : "None"}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground block text-[11px]">Backup Recovery Codes:</span>
                    <span className="font-medium text-foreground">
                      {isMfaActive ? `${mfaStatus?.backupCodesCount || 0} remaining` : "Not generated"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 text-xs space-y-1">
                <p className="font-medium text-primary flex items-center gap-1.5">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  SOC2 Compliance Note
                </p>
                <p className="text-muted-foreground text-[11px] leading-relaxed">
                  Platform operators have sovereign access across all tenant organizations. Enabling MFA is strongly recommended to protect tenant isolation.
                </p>
              </div>
            </CardContent>
          </div>

          <CardFooter className="pt-4 border-t border-border/40 flex gap-3">
            {isMfaActive ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleStartSetup}
                  className="flex-1 gap-1.5 text-xs"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  Reconfigure 2FA
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    setDisableError(null);
                    setDisablePassword("");
                    setDisableModalOpen(true);
                  }}
                  className="flex-1 gap-1.5 text-xs"
                >
                  <ShieldAlert className="h-3.5 w-3.5" />
                  Disable 2FA
                </Button>
              </>
            ) : (
              <Button
                variant="default"
                onClick={handleStartSetup}
                disabled={mfaLoading}
                className="w-full gap-2"
              >
                <QrCode className="h-4 w-4" />
                Set Up Authenticator App
              </Button>
            )}
          </CardFooter>
        </Card>
      </div>

      {/* TOTP Setup Modal */}
      <Dialog open={setupModalOpen} onOpenChange={setSetupModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5 text-primary" />
              Configure Authenticator App
            </DialogTitle>
            <DialogDescription>
              Scan the QR code below using your authenticator app, or enter the secret key manually.
            </DialogDescription>
          </DialogHeader>

          {setupData && (
            <div className="space-y-4 py-2">
              {/* QR Code Presentation */}
              <div className="flex flex-col items-center justify-center py-2">
                <QrCodeSvg value={setupData.provisioningUri} size={180} />
                <p className="text-[11px] text-muted-foreground mt-2 text-center">
                  Scan with 1Password, Google Authenticator, or Apple Keychain
                </p>
              </div>

              {/* Manual Secret Key */}
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Manual Setup Key:</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-2.5 py-1.5 rounded-md bg-muted text-xs font-mono select-all break-all border border-border">
                    {setupData.secret}
                  </code>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(setupData.secret, setCopiedSecret)}
                    className="h-8 shrink-0 gap-1 text-xs"
                  >
                    {copiedSecret ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
                    {copiedSecret ? "Copied" : "Copy"}
                  </Button>
                </div>
              </div>

              {/* Verification Code Form */}
              <form id="totp-confirm-form" onSubmit={handleConfirmTotp} className="space-y-3 pt-2 border-t border-border/40">
                {verifyError && (
                  <Alert variant="destructive" className="py-2">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="text-xs">{verifyError}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-1.5">
                  <Label htmlFor="verify-code" className="text-xs font-semibold">
                    Enter 6-Digit Code from App
                  </Label>
                  <Input
                    id="verify-code"
                    type="text"
                    maxLength={6}
                    pattern="[0-9]{6}"
                    placeholder="123456"
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ""))}
                    className="text-center font-mono tracking-widest text-lg h-11"
                    autoFocus
                    required
                  />
                </div>
              </form>
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setSetupModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="totp-confirm-form"
              disabled={verifyLoading || verifyCode.length !== 6}
              className="gap-2"
            >
              {verifyLoading ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  Verify & Activate
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Backup Recovery Codes Modal */}
      <Dialog open={backupCodesModalOpen} onOpenChange={setBackupCodesModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-500">
              <CheckCircle2 className="h-5 w-5" />
              2FA Activated Successfully!
            </DialogTitle>
            <DialogDescription>
              Save these one-time backup recovery codes in a safe place. If you lose access to your device, they are the only way to recover your administrator account.
            </DialogDescription>
          </DialogHeader>

          <div className="py-2 space-y-3">
            <div className="p-3 bg-muted/40 rounded-xl border border-border grid grid-cols-2 gap-2 text-center font-mono text-xs">
              {backupCodesList.map((code, idx) => (
                <div key={idx} className="p-1.5 bg-background rounded border border-border/60">
                  {code}
                </div>
              ))}
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard(backupCodesList.join("\n"), setCopiedBackupCodes)}
                className="flex-1 gap-1.5 text-xs"
              >
                {copiedBackupCodes ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
                {copiedBackupCodes ? "Codes Copied" : "Copy Codes"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={downloadBackupCodes}
                className="flex-1 gap-1.5 text-xs"
              >
                <Download className="h-3.5 w-3.5" />
                Download (.txt)
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button
              className="w-full"
              onClick={() => setBackupCodesModalOpen(false)}
            >
              I have saved my backup codes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Step-Up Password Modal to Disable MFA */}
      <Dialog open={disableModalOpen} onOpenChange={setDisableModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-500">
              <ShieldAlert className="h-5 w-5" />
              Disable Two-Factor Authentication
            </DialogTitle>
            <DialogDescription>
              Step-up verification required. Please enter your administrator password to confirm turning off 2FA.
            </DialogDescription>
          </DialogHeader>

          <form id="disable-mfa-form" onSubmit={handleDisableMfa} className="space-y-4 py-2">
            {disableError && (
              <Alert variant="destructive" className="py-2">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-xs">{disableError}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="disable-password">Administrator Password</Label>
              <Input
                id="disable-password"
                type="password"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
                placeholder="••••••••••••"
                required
                autoFocus
              />
            </div>
          </form>

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setDisableModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              type="submit"
              form="disable-mfa-form"
              disabled={disableLoading || !disablePassword}
              className="gap-2"
            >
              {disableLoading ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                "Confirm & Disable"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}

export default SecuritySettingsPage;
